import { AlertTriangle, Heart, LogOut, Menu, MessageSquare, Search, ShieldCheck, ShoppingCart, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useLogoutMutation } from "@/features/auth/hooks/useAuthMutations";
import { FloatingAccountDock } from "@/features/auth/components/AccountManagementPanel";
import { SmartSearchCommandPalette } from "@/features/smart-search/components/SmartSearchCommandPalette";
import { useMsnUnreadCount } from "@/features/messages/hooks/useMessages";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/lib/auth/authStore";
import { getMediaUrl } from "@/shared/utils/mediaUtils";
import { i18n, type AppLanguage } from "@/i18n";
import { useThemeStore, type AppTheme } from "@/lib/theme/themeStore";
import { Button, Input } from "@/shared/ui";
import { HybridModeBanner }   from "@/shared/ui/HybridModeBanner";
import { PromoRibbonStrip }  from "@/shared/ui/PromoRibbonStrip";
import { NotificationPopover } from "@/features/notifications/NotificationPopover";
import { formatNavBadgeCount, useNavBadges } from "@/features/dashboard/hooks/useNavBadges";
import { SidebarNav }         from "@/app/layouts/SidebarNav";
import { Footer }             from "@/app/layouts/Footer";
import { FloatingVideoCall }  from "@/features/appointments/components/FloatingVideoCall";
import { FloatingShareControl } from "@/shared/ui/FloatingShareControl";

const themeOptions: Array<{ value: AppTheme; labelKey: string }> = [
  { value: "light-flat", labelKey: "themes.lightFlat" },
  { value: "dark", labelKey: "themes.dark" },
  { value: "gradient-light", labelKey: "themes.gradientLight" },
];

const languageOptions: AppLanguage[] = ["sq", "en", "sr", "tr"];
const marketplaceBadgeAccountTypes = ["basic", "patient"] as const;
const publicContentRoutePrefixes = ["/showcase", "/search", "/businesses", "/doctors"] as const;

const isPublicContentRoute = (pathname: string): boolean => {
  if (pathname === "/") {
    return true;
  }
  return publicContentRoutePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

/**
 * Shell kryesor per rruget e autentikuara `/app/*`.
 */
interface AppShellProps {
  children?: ReactNode;
}

export const AppShell = ({ children }: AppShellProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    isSidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar,
    user,
    capabilities,
    activeFunctionalAccount,
  } = useAuthStore();
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);
  const logoutMutation = useLogoutMutation();
  const activeFunctionalAccountType = activeFunctionalAccount?.functional_account_type ?? null;
  const canUseMarketplaceBadges =
    activeFunctionalAccountType !== null &&
    (marketplaceBadgeAccountTypes.includes(activeFunctionalAccountType as (typeof marketplaceBadgeAccountTypes)[number]) ||
      capabilities.includes("marketplace.cart") ||
      capabilities.includes("marketplace.favorites"));
  const navBadgesQuery = useNavBadges(canUseMarketplaceBadges);
  const favoriteBadgeCount = Number(navBadgesQuery.data?.counts.favorites ?? 0);
  const cartBadgeCount = Number(navBadgesQuery.data?.counts.cart ?? 0);
  const canUseMessaging = capabilities.some(
    (capability) => capability === "messages.general" || capability === "messages.clinical",
  );
  const { data: unreadMessages = 0 } = useMsnUnreadCount(canUseMessaging);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSmartSearchOpen, setIsSmartSearchOpen] = useState(false);
  // isNotificationsOpen removed â€” replaced by NotificationBell component
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const accountMenuPanelRef = useRef<HTMLDivElement>(null);
  const topChromeRef = useRef<HTMLDivElement>(null);
  const sidebarCollapsePreferenceBeforePublicRoute = useRef<boolean | null>(null);
  const activePublicRouteRef = useRef<string | null>(null);
  const [topChromeHeight, setTopChromeHeight] = useState(64);
  // Admin/SuperAdmin kalojnÃ« onboarding-un â€” nuk kanÃ« nevojÃ« pÃ«r profil aktiv
  const displayName = user?.fullName ?? user?.email ?? t("common.session");
  const accountInitial = (displayName.trim().charAt(0) || "U").toUpperCase();
  const accountAvatarUrl = getMediaUrl(user?.avatarUrl ?? null);
  const isSupportMode = Boolean(user?.isImpersonating);
  const openSmartSearch = (): void => {
    setIsSmartSearchOpen(true);
  };

  useEffect(() => {
    const onDocumentKeyDown = (event: KeyboardEvent): void => {
      const isSearchShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
      if (!isSearchShortcut) {
        return;
      }
      event.preventDefault();
      setIsSmartSearchOpen(true);
    };

    document.addEventListener("keydown", onDocumentKeyDown);
    return () => document.removeEventListener("keydown", onDocumentKeyDown);
  }, []);

  useEffect(() => {
    if (user === null) {
      sidebarCollapsePreferenceBeforePublicRoute.current = null;
      activePublicRouteRef.current = null;
      return;
    }

    if (isPublicContentRoute(pathname)) {
      if (activePublicRouteRef.current !== pathname) {
        sidebarCollapsePreferenceBeforePublicRoute.current = useAuthStore.getState().isSidebarCollapsed;
        activePublicRouteRef.current = pathname;
        setSidebarCollapsed(true);
      }
      return;
    }

    activePublicRouteRef.current = null;
    if (sidebarCollapsePreferenceBeforePublicRoute.current !== null) {
      setSidebarCollapsed(sidebarCollapsePreferenceBeforePublicRoute.current);
      sidebarCollapsePreferenceBeforePublicRoute.current = null;
    }
  }, [pathname, setSidebarCollapsed, user]);

  useEffect(() => {
    if (!isAccountMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      const isInsideDesktopMenu = accountMenuRef.current?.contains(target) ?? false;
      const isInsideMobileMenu = accountMenuPanelRef.current?.contains(target) ?? false;
      if (!isInsideDesktopMenu && !isInsideMobileMenu) {
        setIsAccountMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isAccountMenuOpen]);

  useEffect(() => {
    const updateTopChromeHeight = (): void => {
      const height = topChromeRef.current?.getBoundingClientRect().height ?? 64;
      setTopChromeHeight(Math.ceil(height));
    };

    updateTopChromeHeight();

    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateTopChromeHeight);
    if (topChromeRef.current && observer) {
      observer.observe(topChromeRef.current);
    }
    window.addEventListener("resize", updateTopChromeHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateTopChromeHeight);
    };
  }, []);

  const supportTopOffset = isSupportMode ? 40 : 0;
  const topChromeTopClass = isSupportMode ? "top-10" : "top-0";
  const sidebarTopOffset = supportTopOffset + topChromeHeight;

  return (
    <div className={cn("min-h-screen bg-background text-[hsl(var(--text))]", isSupportMode && "pt-10")}>
      {isSupportMode ? (
        <div className="fixed inset-x-0 top-0 z-[60] flex min-h-10 items-center justify-center gap-2 bg-red-700 px-4 py-2 text-center text-sm font-semibold text-white shadow-lg">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Keni hyrÃ« si {displayName}. Qasja nÃ« tÃ« dhÃ«nat klinike Ã«shtÃ« e bllokuar automatikisht.</span>
        </div>
      ) : null}
      <div ref={topChromeRef} className={cn("fixed inset-x-0 z-40", topChromeTopClass)}>
        <PromoRibbonStrip />
        <header className="border-b border-border bg-[hsl(var(--surface))]/95 backdrop-blur-xl">
        <div className="flex h-16 min-w-0 items-center gap-2 px-3 sm:gap-3 sm:px-4">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => setIsMobileNavOpen(true)}
            aria-label="Hap navigimin"
            className="md:hidden"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={toggleSidebar}
            aria-label="Hap/mbyll navigimin"
            className="hidden md:inline-flex"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
          <Link
            to="/"
            onClick={() => {
              if (user !== null) {
                setSidebarCollapsed(true);
              }
            }}
            className="flex min-w-0 shrink items-center gap-2 font-bold text-primary transition hover:opacity-80"
          >
            <ShieldCheck className="h-7 w-7 shrink-0" aria-hidden="true" />
            <span className="hidden truncate sm:inline">{t("common.appName")}</span>
          </Link>
          <div className="hidden min-w-0 flex-1 justify-center md:flex">
            <div className="flex w-full max-w-xl items-center">
            <div className="relative w-full">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                className="h-10 pl-10"
                placeholder={t("common.searchPlaceholder")}
                value={globalSearch}
                onChange={(event) => setGlobalSearch(event.target.value)}
                onFocus={openSmartSearch}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    openSmartSearch();
                  }
                }}
              />
            </div>
            <Button type="button" variant="ghost" className="ml-2 shrink-0" onClick={openSmartSearch}>
              {t("common.search")}
            </Button>
            </div>
          </div>
          <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1 sm:gap-2 md:ml-0">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={t("common.search")}
              onClick={openSmartSearch}
              className="md:hidden"
            >
              <Search className="h-5 w-5" aria-hidden="true" />
            </Button>
            <div className="relative">
              <Button type="button" size="icon" variant="ghost" aria-label={t("common.favorites")} onClick={() => navigate("/app/favorites")}>
                <Heart className="h-5 w-5" aria-hidden="true" />
              </Button>
              {favoriteBadgeCount > 0 ? (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground ring-1 ring-background">
                  {formatNavBadgeCount(favoriteBadgeCount)}
                </span>
              ) : null}
            </div>
            <NotificationPopover />
            <div className="relative">
              <Button type="button" size="icon" variant="ghost" aria-label={t("common.messages")} onClick={() => navigate("/app/messages")}>
                <MessageSquare className="h-5 w-5" aria-hidden="true" />
              </Button>
              {unreadMessages > 0 ? (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                  {unreadMessages > 9 ? "9+" : unreadMessages}
                </span>
              ) : null}
            </div>
            <div className="relative">
              <Button type="button" size="icon" variant="ghost" aria-label={t("common.cart")} onClick={() => navigate("/app/cart")}>
                <ShoppingCart className="h-5 w-5" aria-hidden="true" />
              </Button>
              {cartBadgeCount > 0 ? (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground ring-1 ring-background">
                  {formatNavBadgeCount(cartBadgeCount)}
                </span>
              ) : null}
            </div>
            <div ref={accountMenuRef} className="relative">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Hap menunÃ« e llogarisÃ«"
                aria-expanded={isAccountMenuOpen}
                onClick={() => setIsAccountMenuOpen((current) => !current)}
                className="rounded-full border border-border bg-card shadow-sm"
              >
                <AccountAvatar avatarUrl={accountAvatarUrl} displayName={displayName} fallback={accountInitial} sizeClassName="h-8 w-8 text-sm" />
              </Button>
              {isAccountMenuOpen ? (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-[64] hidden cursor-default md:block"
                    aria-label="Mbyll menunÃ« e llogarisÃ«"
                    onClick={() => setIsAccountMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-12 z-[72] hidden w-80 rounded-[24px] border border-border bg-card p-4 text-card-foreground shadow-2xl md:block">
                    <AccountMenuContent
                      displayName={displayName}
                      email={user?.email ?? null}
                      avatarUrl={accountAvatarUrl}
                      theme={theme}
                      setTheme={setTheme}
                      onClose={() => setIsAccountMenuOpen(false)}
                      onNavigate={(path) => { navigate(path); setIsAccountMenuOpen(false); }}
                      onLogout={() => { setIsAccountMenuOpen(false); logoutMutation.mutate(); }}
                      logoutDisabled={logoutMutation.isPending}
                      t={t}
                    />
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
        </header>
      </div>

      {isAccountMenuOpen ? (
        <div className="fixed inset-0 z-[72] md:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-background/70 backdrop-blur-sm"
            aria-label="Mbyll menunÃ« e llogarisÃ«"
            onClick={() => setIsAccountMenuOpen(false)}
          />
          <div ref={accountMenuPanelRef} className="fixed inset-x-3 bottom-3 w-auto overflow-y-auto rounded-[24px] border border-border bg-card p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] text-card-foreground shadow-2xl [max-height:min(70vh,520px)]">
            <AccountMenuContent
              displayName={displayName}
              email={user?.email ?? null}
              avatarUrl={accountAvatarUrl}
              theme={theme}
              setTheme={setTheme}
              onClose={() => setIsAccountMenuOpen(false)}
              onNavigate={(path) => { navigate(path); setIsAccountMenuOpen(false); }}
              onLogout={() => { setIsAccountMenuOpen(false); logoutMutation.mutate(); }}
              logoutDisabled={logoutMutation.isPending}
              t={t}
            />
          </div>
        </div>
      ) : null}

      <SmartSearchCommandPalette
        open={isSmartSearchOpen}
        query={globalSearch}
        onQueryChange={setGlobalSearch}
        onClose={() => setIsSmartSearchOpen(false)}
      />

      {isMobileNavOpen ? (
        <div className="fixed inset-0 z-[68] md:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-background/70 backdrop-blur-sm"
            aria-label="Mbyll navigimin"
            onClick={() => setIsMobileNavOpen(false)}
          />
          <aside className="relative flex h-full w-80 max-w-[86vw] flex-col overflow-y-auto border-r border-border bg-card p-4 text-card-foreground shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2 font-bold text-primary">
                <ShieldCheck className="h-7 w-7" aria-hidden="true" />
                <span>{t("common.appName")}</span>
              </Link>
              <Button type="button" size="icon" variant="ghost" aria-label="Mbyll navigimin" onClick={() => setIsMobileNavOpen(false)}>
                <X className="h-5 w-5" aria-hidden="true" />
              </Button>
            </div>
            <SidebarNav onNavigate={() => setIsMobileNavOpen(false)} />
          </aside>
        </div>
      ) : null}

      <div className="flex" style={{ paddingTop: topChromeHeight }}>
        <aside
          className={cn(
            "fixed left-0 z-30 hidden shrink-0 overflow-y-auto border-r border-border bg-[hsl(var(--surface))]/90 p-3 pb-10 backdrop-blur-xl transition-all md:block",
            isSidebarCollapsed ? "w-16" : "w-72",
          )}
          style={{ top: sidebarTopOffset, height: `calc(100vh - ${sidebarTopOffset}px)` }}
        >
          <SidebarNav collapsed={isSidebarCollapsed} />
        </aside>

        <div className={cn("flex min-w-0 flex-1 flex-col overflow-x-hidden transition-[margin] duration-200", isSidebarCollapsed ? "md:ml-16" : "md:ml-72")}>
        <HybridModeBanner />
        <main className="min-w-0 flex-1 overflow-x-hidden p-4 lg:p-6">
          {children ?? <Outlet />}
          <Footer />
          {/* PiP video â€” persist-on jashtÃ« /app/messages */}
          <FloatingVideoCall />
        </main>
        </div>
      </div>
      <FloatingAccountDock />
      <FloatingShareControl />
    </div>
  );
};

function AccountMenuContent({
  displayName,
  email,
  avatarUrl,
  theme,
  setTheme,
  onClose,
  onNavigate,
  onLogout,
  logoutDisabled,
  t,
}: {
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
  theme: AppTheme;
  setTheme: (theme: AppTheme) => void;
  onClose: () => void;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  logoutDisabled: boolean;
  t: (key: string) => string;
}): JSX.Element {
  return (
    <div className="min-w-0 text-sm">
      <div className="flex min-w-0 items-center gap-2.5 border-b border-border pb-3">
        <button
          type="button"
          onClick={() => onNavigate("/app/account")}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-2xl p-1.5 text-left transition hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
          aria-label="Hap llogarine"
        >
        <AccountAvatar
          avatarUrl={avatarUrl}
          displayName={displayName}
          fallback={(displayName.trim().charAt(0) || "U").toUpperCase()}
          sizeClassName="h-10 w-10 text-base"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-card-foreground">{displayName}</p>
          <p className="truncate text-xs text-muted-foreground">{email ?? "â€”"}</p>
        </div>
        </button>
        <Button type="button" size="icon" variant="ghost" aria-label="Mbyll menunÃ« e llogarisÃ«" onClick={onClose} className="h-8 w-8 shrink-0">
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {/* <div className="mt-3 grid gap-1.5">
        <button
          type="button"
          onClick={() => onNavigate("/app/account")}
          className="flex w-full items-center gap-2.5 rounded-2xl px-3 py-2 text-left text-card-foreground transition hover:bg-secondary"
        >
          <UserCircle className="h-4 w-4 text-primary" aria-hidden="true" />
          <span>Llogaria / Profili</span>
        </button>
        <button
          type="button"
          onClick={() => onNavigate("/app/account/security")}
          className="flex w-full items-center gap-2.5 rounded-2xl px-3 py-2 text-left text-card-foreground transition hover:bg-secondary"
        >
          <Settings className="h-4 w-4 text-primary" aria-hidden="true" />
          <span>Siguria dhe cilÃ«simet</span>
        </button>
      </div> */}

      <div className="mt-3 border-t border-border pt-3">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t("common.theme")}</p>
        <div className="grid grid-cols-3 gap-1.5">
          {themeOptions.map((option) => (
            <Button
              key={option.value}
              type="button"
              size="sm"
              variant={theme === option.value ? "primary" : "outline"}
              className="h-8 rounded-[20px] px-2 text-[11px]"
              onClick={() => setTheme(option.value)}
            >
              {t(option.labelKey)}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t("common.language")}</p>
        <div className="grid grid-cols-4 gap-1.5">
          {languageOptions.map((language) => (
            <Button
              key={language}
              type="button"
              size="sm"
              variant={i18n.language === language ? "primary" : "outline"}
              className="h-8 rounded-[20px] px-2 text-[11px]"
              onClick={() => void i18n.changeLanguage(language)}
            >
              {t(`languages.${language}`)}
            </Button>
          ))}
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="mt-3 h-9 w-full justify-center rounded-2xl border-destructive/30 text-destructive hover:bg-destructive/10"
        disabled={logoutDisabled}
        onClick={onLogout}
      >
        <LogOut className="h-4 w-4" aria-hidden="true" />
        {t("common.logout")}
      </Button>
    </div>
  );
}

function AccountAvatar({
  avatarUrl,
  displayName,
  fallback,
  sizeClassName,
}: {
  avatarUrl: string | null;
  displayName: string;
  fallback: string;
  sizeClassName: string;
}): JSX.Element {
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [avatarUrl]);

  return (
    <span className={cn("grid shrink-0 place-items-center overflow-hidden rounded-full bg-primary font-bold text-primary-foreground", sizeClassName)}>
      {avatarUrl && !hasImageError ? (
        <img
          src={avatarUrl}
          alt={displayName}
          className="h-full w-full object-cover"
          onError={() => setHasImageError(true)}
        />
      ) : (
        fallback
      )}
    </span>
  );
}
